import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet } from '@/lib/api';
import { Search, BookOpen, ArrowRight } from 'lucide-react';

const BRAND_LOGO = '/brand/logo.png';

export default function SEOLearnHub() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [boardOptions, setBoardOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [classFilter, setClassFilter] = useState('all');
  const [boardFilter, setBoardFilter] = useState('all'); // 'all' | 'generic' | <board_slug>

  useEffect(() => {
    document.title = 'Learn any subject, any class — Gurukul Veda';
    const setMeta = (name, content, attr = 'name') => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Browse every subject and class covered by Gurukul Veda — free AI tutor for CBSE, ICSE, IB, and all Indian state boards. Ask doubts, snap worksheets, learn step-by-step.');
    apiGet('/public/seo/index').then(d => {
      setItems(d.items || []);
      setBoardOptions(d.boards || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const classes = useMemo(() => Array.from(new Set(items.map(i => String(i.class_level)))).sort((a, b) => parseInt(a) - parseInt(b)), [items]);

  const filtered = useMemo(() => {
    return items.filter(i => {
      if (classFilter !== 'all' && String(i.class_level) !== classFilter) return false;
      if (boardFilter === 'generic' && i.board_slug) return false;
      if (boardFilter !== 'all' && boardFilter !== 'generic' && i.board_slug !== boardFilter) return false;
      if (q && !i.subject.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [items, q, classFilter, boardFilter]);

  // Group by class then subject
  const byClass = useMemo(() => {
    const map = {};
    filtered.forEach(i => {
      const k = String(i.class_level);
      if (!map[k]) map[k] = [];
      map[k].push(i);
    });
    return map;
  }, [filtered]);

  return (
    <div className="min-h-screen bg-[#FEFBF4] text-slate-800" data-testid="seo-hub-page" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FEFBF4]/80 border-b border-slate-900/5">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="seo-hub-logo">
            <img src={BRAND_LOGO} alt="Gurukul Veda" className="w-9 h-9 object-contain" />
            <div>
              <p className="font-display text-[17px] font-semibold text-slate-900 leading-tight tracking-tight">Gurukul Veda</p>
              <p className="text-[10px] text-slate-500 leading-tight -mt-0.5">Your Personal Study Companion</p>
            </div>
          </Link>
          <button onClick={() => navigate('/login')} data-testid="seo-hub-cta"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-heading hover:bg-slate-800 transition-all">
            Start free <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 pb-8">
        <p className="font-heading text-[11px] uppercase tracking-[0.3em] text-indigo-600 mb-3">Learn</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 leading-[1.05] tracking-[-0.03em]" data-testid="seo-hub-h1">
          Every subject. Every class. <span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">Free to start.</span>
        </h1>
        <p className="mt-5 text-lg text-slate-600 max-w-2xl">Your personal AI tutor — aligned to CBSE, ICSE, IB and all Indian state boards. Pick your class below and dive in.</p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input data-testid="seo-hub-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search subject — Maths, Physics, Biology…"
              className="w-full pl-11 pr-4 py-3 rounded-full bg-white border border-slate-200 text-sm focus:outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100" />
          </div>
          <select data-testid="seo-hub-class-filter" value={classFilter} onChange={e => setClassFilter(e.target.value)}
            className="px-4 py-3 rounded-full bg-white border border-slate-200 text-sm font-heading focus:outline-none focus:border-indigo-300">
            <option value="all">All classes</option>
            {classes.map(c => <option key={c} value={c}>Class {c}</option>)}
          </select>
          <select data-testid="seo-hub-board-filter" value={boardFilter} onChange={e => setBoardFilter(e.target.value)}
            className="px-4 py-3 rounded-full bg-white border border-slate-200 text-sm font-heading focus:outline-none focus:border-indigo-300">
            <option value="all">All boards</option>
            <option value="generic">All-boards pages</option>
            {boardOptions.map(b => <option key={b.slug} value={b.slug}>{b.label}</option>)}
          </select>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-16">
        {loading && <div className="text-center py-20 text-slate-400 text-sm">Loading curriculum…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-20 text-slate-400 text-sm">No subjects match your filters.</div>
        )}
        {!loading && Object.keys(byClass).sort((a, b) => parseInt(a) - parseInt(b)).map(cls => (
          <div key={cls} className="mb-12" data-testid={`seo-hub-class-${cls}`}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 text-white flex items-center justify-center font-heading font-bold text-sm">{cls}</div>
              <h2 className="font-display text-2xl font-bold text-slate-900 tracking-tight">Class {cls}</h2>
              <span className="text-xs text-slate-400">{byClass[cls].length} subject{byClass[cls].length !== 1 ? 's' : ''}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {byClass[cls].map(i => (
                <Link key={i.url} to={i.url} data-testid={`seo-hub-card-${i.board_slug || 'all'}-${i.subject_slug}-${i.class_level}`}
                  className="group p-4 rounded-2xl bg-white border border-slate-100 hover:border-indigo-200 hover:shadow-md transition-all flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-4.5 h-4.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-heading text-sm font-semibold text-slate-800 group-hover:text-indigo-600 transition-colors truncate">{i.subject}</p>
                      {i.board_label && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 text-[9px] font-heading uppercase tracking-wider flex-shrink-0">{i.board_label}</span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-400 font-body">{i.topic_count} chapters{!i.board_label ? ' · All boards' : ''}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>

      <footer className="py-12 border-t border-slate-900/5">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-wrap items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={BRAND_LOGO} alt="Gurukul Veda" className="w-8 h-8" />
            <p className="font-display text-base font-semibold text-slate-900">Gurukul Veda</p>
          </Link>
          <p className="text-[11px] text-slate-400">© {new Date().getFullYear()} Gurukul Veda · Made for Indian students.</p>
        </div>
      </footer>
    </div>
  );
}
